import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { configureAmplify } from './config/amplify.js';
import { AuthProvider } from './auth/AuthContext.jsx';
import ProtectedRoute from './auth/ProtectedRoute.jsx';

// EAGER — always needed for first paint / route infrastructure
import AppLayout from './components/AppLayout.jsx';
import UpdateBanner from './components/UpdateBanner.jsx';
import RouteErrorBoundary from './components/ErrorBoundary/RouteErrorBoundary.jsx';
import LoadingSpinner from './components/LoadingSpinner.jsx';
import useVersionCheck from './hooks/useVersionCheck.js';
import { logger } from './lib/logger.js';

// EAGER pages — auth + landing are first-paint critical; load immediately
import LoginPage from './pages/LoginPage.jsx';
import RegisterPage from './pages/RegisterPage.jsx';
import ForgotPasswordPage from './pages/ForgotPasswordPage.jsx';
import LandingPage from './pages/LandingPage.jsx';

// ---------------------------------------------------------------------------
// LAZY pages — loaded on demand when the user navigates to the route.
// Vite splits each lazy import into its own JS chunk, reducing the initial
// entry bundle size (AC-CODE-LAZY-ROUTES, TASK-079 Phase 4).
// ---------------------------------------------------------------------------
const AssessmentPage = lazy(() => import('./modules/assessment/AssessmentPage.jsx'));
const TrackerPage = lazy(() => import('./modules/tracker/TrackerPage.jsx'));
const TechSurveyPage = lazy(() => import('./modules/techSurvey/TechSurveyPage.jsx'));
const TechSurveyAdminPage = lazy(() => import('./pages/admin/TechSurveyAdminPage.jsx'));
const AiHealthReportPage = lazy(() => import('./pages/admin/AiHealthReportPage.jsx'));
const InitiativeDashboard = lazy(() => import('./pages/admin/InitiativeDashboard.jsx'));
const ProfilePage = lazy(() => import('./pages/ProfilePage.jsx'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage.jsx'));
const OpCoListPage = lazy(() => import('./pages/admin/OpCoListPage.jsx'));
const OpCoDetailPage = lazy(() => import('./pages/admin/OpCoDetailPage.jsx'));
const ScorecardDashboard = lazy(() => import('./pages/admin/ScorecardDashboard.jsx'));
const ScorecardGroupDetail = lazy(() => import('./pages/admin/ScorecardGroupDetail.jsx'));
const QuestionSetsPage = lazy(() => import('./pages/admin/QuestionSetsPage.jsx'));
const QuestionSetEditorPage = lazy(() => import('./pages/admin/QuestionSetEditorPage.jsx'));
const TeamPage = lazy(() => import('./pages/TeamPage.jsx'));
const WelcomePage = lazy(() => import('./pages/WelcomePage.jsx'));
const WorkshopAdminPage = lazy(() => import('./modules/workshops/admin/WorkshopAdminPage.jsx'));
const WorkshopRecordingsPage = lazy(() => import('./modules/workshops/admin/WorkshopRecordingsPage.jsx'));
const WorkshopAdminDetailPage = lazy(() => import('./modules/workshops/admin/WorkshopAdminDetailPage.jsx'));
const WorkshopCatalogPage = lazy(() => import('./modules/workshops/WorkshopCatalogPage.jsx'));
const WorkshopDetailPage = lazy(() => import('./modules/workshops/WorkshopDetailPage.jsx'));
const TeachingSessionsPage = lazy(() => import('./modules/teaching/TeachingSessionsPage.jsx'));
const TeachingSessionDetailPage = lazy(() => import('./modules/teaching/TeachingSessionDetailPage.jsx'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage.jsx'));
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage.jsx'));
const PipelineOverviewPage = lazy(() => import('./pages/admin/PipelineOverviewPage.jsx'));
const CommunicationsPage = lazy(() => import('./pages/admin/CommunicationsPage.jsx'));
const CommunicationDetailPage = lazy(() => import('./pages/admin/CommunicationDetailPage.jsx'));
const ComposePage = lazy(() => import('./pages/admin/ComposePage.jsx'));
const SandboxPage = lazy(() => import('./pages/SandboxPage.jsx'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.jsx'));
const ServiceKeysPage = lazy(() => import('./pages/admin/ServiceKeysPage.jsx'));
const FeedbackPage = lazy(() => import('./pages/admin/FeedbackPage.jsx'));
const BmbTelemetryDashboard = lazy(() => import('./pages/admin/BmbTelemetryDashboard.jsx'));

configureAmplify();

logger.info('app-mount', {
  version: typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : 'dev',
});

const SANDBOX_ENABLED = import.meta.env.VITE_SANDBOX_ENABLED === 'true';

export default function App() {
  const { updateAvailable, reload } = useVersionCheck();

  return (
    <AuthProvider>
      <BrowserRouter>
        {updateAvailable && <UpdateBanner onReload={reload} />}
        <Routes>
          <Route
            path="/login"
            element={
              <RouteErrorBoundary>
                <LoginPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/register"
            element={
              <RouteErrorBoundary>
                <RegisterPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <RouteErrorBoundary>
                <ForgotPasswordPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute skipGroupCheck>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <RouteErrorBoundary>
                  <LandingPage />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="welcome"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <WelcomePage />
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="assessment"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={["ceo", "admin", "leader"]} redirectTo="/">
                      <AssessmentPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="tracker"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={["ceo", "tech_leader", "admin", "leader"]} redirectTo="/">
                      <TrackerPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            {/* Deep-link survey page (emailed link). No role guard here on purpose: the
                page itself explains wrong-role / no-OpCo states instead of "Access Denied". */}
            <Route
              path="tech-stack"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <TechSurveyPage />
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="team"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={["ceo", "admin", "leader", "tech_leader"]}>
                      <TeamPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="profile"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProfilePage />
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            {/* /admin used to be the assessment dashboard; AI Health replaced it. */}
            <Route path="admin" element={<Navigate to="/admin/ai-assessment" replace />} />
            <Route
              path="admin/ai-assessment/:groupSlug?"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={["admin", "leader"]}>
                      <AiHealthReportPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/initiatives"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={["admin", "leader"]}>
                      <InitiativeDashboard />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/tech-survey"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={["admin", "leader"]}>
                      <TechSurveyAdminPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/users"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={["admin", "leader"]}>
                      <UsersPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />

            <Route
              path="admin/org"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={["admin", "leader"]}>
                      <OpCoListPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/org/:opcoId"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={["admin", "leader"]}>
                      <OpCoDetailPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/question-sets"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole="admin">
                      <QuestionSetsPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/question-sets/new"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole="admin">
                      <QuestionSetEditorPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/question-sets/:id/edit"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole="admin">
                      <QuestionSetEditorPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/scorecard"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={["admin", "leader"]}>
                      <ScorecardDashboard />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/scorecard/:groupId"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={["admin", "leader"]}>
                      <ScorecardGroupDetail />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/workshops/:id"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole="admin">
                      <WorkshopAdminDetailPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/workshops"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole="admin">
                      <WorkshopAdminPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/workshop-recordings"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={['admin', 'leader']}>
                      <WorkshopRecordingsPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="workshops"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <WorkshopCatalogPage />
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="workshops/:id"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <WorkshopDetailPage />
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            {/* FEAT-067 Teaching module. No requireRole — "instructor" is a derived
                capability, not a login role (Approach A). Nav visibility is gated on
                isInstructor; the API authorizes each request on true assignment (403). */}
            <Route
              path="teaching/sessions"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <TeachingSessionsPage />
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="teaching/sessions/:wid/:sid"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <TeachingSessionDetailPage />
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/analytics"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole="admin">
                      <AnalyticsPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/pipeline"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole={["admin", "leader"]}>
                      <PipelineOverviewPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/communications"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole="admin">
                      <CommunicationsPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/service-keys"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole="admin">
                      <ServiceKeysPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/feedback"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole="admin">
                      <FeedbackPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/bmb-telemetry"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole="admin">
                      <BmbTelemetryDashboard />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/communications/compose"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole="admin">
                      <ComposePage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="admin/communications/:id"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProtectedRoute requireRole="admin">
                      <CommunicationDetailPage />
                    </ProtectedRoute>
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="notifications"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <NotificationsPage />
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
            {SANDBOX_ENABLED && (
              <Route
                path="sandbox/error"
                element={
                  <RouteErrorBoundary>
                    <Suspense fallback={<LoadingSpinner />}>
                      <SandboxPage />
                    </Suspense>
                  </RouteErrorBoundary>
                }
              />
            )}
            {/* Catch-all 404 — must be last inside AppLayout parent route */}
            <Route
              path="*"
              element={
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <NotFoundPage />
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
